// src/pages/Counselling/Requests/[RequestDetails]/index.tsx
/**
 * A single public care request, and the place it is actioned.
 *
 * Layout follows `Counselling/Cases/[CaseDetails]/index.tsx`: Back button, header row carrying Refresh
 * and the status menu, a profile card whose top-right corner belongs to the primary action, then Tabs
 * inside one shared card.
 *
 * ⚠️ **The description is anonymous public input.** The API decodes it server-side, so it is rendered
 * verbatim as **plain text**, never through `dangerouslySetInnerHTML`, unlike the case detail page
 * whose description is written by staff. Server-side decoding changed the encoding, not the fact that
 * anyone on the internet can put anything in this field.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon, Avatar, Badge, Button, Card, Center, Divider, Group, Loader, SimpleGrid, Stack,
  Text, Title, Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft, IconCalendar, IconCheck, IconHeartHandshake, IconInfoCircle,
  IconMail, IconMapPin, IconPhone, IconRefresh, IconUser, IconUsers, IconX,
} from '@tabler/icons-react';
import Tabs from '@/components/ui/navigation/Tabs';
import { tabStyle } from '@/components/ui/TabStyle';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useCareRequestService, useCareRequestStatusService,
  type CareRequestModelView, type CareRequestSubjectView,
} from '@actschurch/ccms-sdk';
import { useLookupStatuses } from '@/pages/Overview/Hooks/useLookupStatuses';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { formatSDKDate } from '@/utils/dateTime';
import { resolveAvatarSrc, getInitialsFromName } from '@/utils/avatar';
import { REQUEST_STATUS } from '@/pages/Counselling/requestStatusIds';
import SubmittedPersonModal, { type SubmittedPerson } from '@/components/common/SubmittedPersonModal';
import { formatBirthDate } from '@/components/common/MemberDetailModal';
import ApproveRequestModal from '../Components/ApproveRequestModal';
import RejectRequestModal from '../Components/RejectRequestModal';
import RequestStatusMenu from '../Components/RequestStatusMenu';

/** Label/value line. Duplicated per detail page in this repo rather than shared. */
function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Box>{icon}</Box>
      <div style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed">{label}</Text>
        {empty
          ? <Text size="sm" c="dimmed">Not set</Text>
          : typeof value === 'string' || typeof value === 'number'
            ? <Text size="sm" fw={600}>{value}</Text>
            : value}
      </div>
    </Group>
  );
}

/** `CareRequestSubjectView` → the shape `SubmittedPersonModal` reads. */
function toPerson(sub: CareRequestSubjectView): SubmittedPerson {
  return {
    id: sub.subjectID,
    firstName: sub.firstName,
    surname: sub.surname,
    email: sub.email,
    phone: sub.phone,
    gender: sub.gender,
    dateOfBirth: sub.dateOfBirth,
    role: sub.role,
    otherChurch: sub.otherChurch,
    isMember: sub.isMember,
    displayLocationName: sub.displayLocationName,
    isMain: sub.isMain,
    memberID: sub.memberID,
    memberName: sub.memberName,
  };
}

/** Local Box so DetailRow's icon column keeps its width without importing another primitive. */
function Box({ children }: { children: React.ReactNode }) {
  return <div style={{ flexShrink: 0, opacity: 0.7, paddingTop: 2 }}>{children}</div>;
}

export default function RequestDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requestService = useCareRequestService();
  const requestStatusService = useCareRequestStatusService();
  const statuses = useLookupStatuses(requestStatusService);
  const { refetch: refetchSidebar } = useSidebarCountsContext();

  const [request, setRequest] = useState<CareRequestModelView | null>(null);
  const [loading, setLoading] = useState(true);
  const [changingStatus, setChangingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  /** Card click opens the read-only person detail. Null when closed. */
  const [detailPerson, setDetailPerson] = useState<SubmittedPerson | null>(null);

  const fetchRequest = useCallback(async () => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    try {
      setRequest(await requestService.getById(Number(id)));
    } catch (e) {
      console.error('[RequestDetails] failed to load:', e);
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [id, requestService]);

  useEffect(() => { fetchRequest(); }, [fetchRequest]);

  const handleChangeStatus = async (statusID: number) => {
    if (!request) return;
    setChangingStatus(true);
    try {
      await requestService.changeStatus(request.requestID, statusID);
      toast.success('Status updated', 'The request status was changed.');
      await fetchRequest();
      refetchSidebar();
    } catch (e) {
      console.error('[RequestDetails] status change failed:', e);
      toast.error('Could not change the status', e instanceof Error ? e.message : String(e));
    } finally {
      setChangingStatus(false);
    }
  };

  const handleApproved = async (caseID: number | null) => {
    await fetchRequest();
    refetchSidebar();
    toast.success(
      'Request approved',
      caseID ? `Case #${caseID} was created.` : 'The case was created.',
      caseID
        ? { action: { label: 'View case', onClick: () => navigate(`/Counselling/Cases/${caseID}`) } }
        : undefined,
    );
  };

  const handleRejected = async () => {
    await fetchRequest();
    refetchSidebar();
  };

  const isPending = request?.requestStatusID === REQUEST_STATUS.Pending;
  const isDecided = useMemo(
    () => request != null && request.requestStatusID !== REQUEST_STATUS.Pending,
    [request],
  );

  // ⚠️ Rendered verbatim: the API decodes `description` server-side, so decoding again here would
  // corrupt any text that merely happens to look Base64.
  const description = request?.description?.trim() ?? '';

  /** Everyone needing care on this request. Populated since SDK 0.0.48. */
  const subjects = request?.subjects ?? [];
  /** The need's own custom-field answers, resolved to labels by the API. */
  const customData = request?.customData ?? [];

  if (!id) return null;

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading request…</Text>
        </Stack>
      </Center>
    );
  }

  if (!request) {
    return (
      <Stack gap="lg">
        <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(-1)}
        // ⚠️ `Stack` defaults to `align="stretch"`, which makes the button full width and centres its
        // label. The Cases detail page avoids this only because it sits in a `Container`.
        style={{ alignSelf: 'flex-start' }}
      >
          Back
        </Button>
        <Center py="xl"><Text c="dimmed">Request not found.</Text></Center>
      </Stack>
    );
  }

  const requesterName = request.fullName
    || [request.firstName, request.surname].filter(Boolean).join(' ').trim()
    || 'Unknown';

  /**
   * The requester as a clickable person, for the case where no separate subjects were staged.
   * Their match lives at the top of the request (`matchedMemberID`), not in `subjects`.
   * ⚠️ `id: 0` is a placeholder — the requester has no `subjectID` of their own.
   *
   * ⚠️ **The requester has no `dateOfBirth` field.** `dateOfBirth` was added to
   * `CareRequestSubjectView` in SDK 0.0.49, but `CareRequestModelView` has no equivalent for the
   * requester themselves — their DOB reaches the backend as `PublicCareRequestSubmission.dob` and is
   * not exposed back. It is recovered from the staged custom answers below when it is there, which is
   * where the public form used to put it before per-subject DOB became first-class.
   */
  const requesterDob = customData.find(
    (cd) => (cd.fieldKey ?? '').toLowerCase().replace(/[\s-]/g, '_') === 'date_of_birth',
  )?.value?.trim() || null;

  /**
   * The person needing care when someone submitted **on behalf of** them.
   *
   * ⚠️ `isForSelf === false` means the requester is not the counselee. The API stages that person in
   * the top-level `subjectFirstName` / `subjectSurname` block, which is **separate from** `subjects[]`
   * and predates it. A request can therefore name a counselee while `subjects[]` is still empty, and
   * treating an empty `subjects[]` as "this is about the requester" names the wrong person entirely.
   *
   * SDK 0.0.54 added `subjectEmail` / `subjectPhone` / `subjectGender` / `subjectRole`, so this person
   * is now shown in full rather than as a bare name. ⚠️ They are still optional — an older request
   * staged before those fields existed carries only the name, and `nameOnly` below detects that case
   * so the modal explains the absence instead of rendering a grid of blanks.
   */
  const onBehalfName = (request.subjectFullName
    || [request.subjectFirstName, request.subjectSurname].filter(Boolean).join(' ').trim()
    || '').trim();
  const isOnBehalf = request.isForSelf === false && onBehalfName !== '';

  const onBehalfAsPerson: SubmittedPerson = {
    id: -1,
    firstName: request.subjectFirstName,
    surname: request.subjectSurname,
    email: request.subjectEmail,
    phone: request.subjectPhone,
    gender: request.subjectGender,
    role: request.subjectRole,
    isMain: true,
    // ⚠️ Only claim "name only" when it is actually true. On a request staged before 0.0.54 every
    // contact field is null and the modal should say the API has nothing; on a newer one it must not.
    nameOnly: !request.subjectEmail && !request.subjectPhone
      && !request.subjectGender && !request.subjectRole,
  };

  const requesterAsPerson: SubmittedPerson = {
    id: 0,
    firstName: request.firstName,
    surname: request.surname,
    email: request.email,
    phone: request.phone,
    gender: request.gender,
    dateOfBirth: requesterDob,
    preferredContactMethod: request.preferredContactMethod,
    isMember: request.isExistingMember,
    isMain: true,
    memberID: request.matchedMemberID,
    memberName: request.matchedMemberName,
  };

  return (
    <Stack gap="lg">
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(-1)}
        // ⚠️ `Stack` defaults to `align="stretch"`, which makes the button full width and centres its
        // label. The Cases detail page avoids this only because it sits in a `Container`.
        style={{ alignSelf: 'flex-start' }}
      >
        Back
      </Button>

      <Group justify="space-between" align="center">
        <Title order={2}>Request Details</Title>
        <Group gap="xs" align="center">
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" size="lg" loading={loading} onClick={fetchRequest}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <RequestStatusMenu
            statuses={statuses}
            currentStatusID={request.requestStatusID}
            busy={changingStatus}
            onChangeStatus={handleChangeStatus}
            onApprove={() => setApproveOpen(true)}
            onReject={() => setRejectOpen(true)}
          />
        </Group>
      </Group>

      {/* ── Profile card ── */}
      <Card withBorder radius="md" padding="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="md" align="flex-start" wrap="nowrap">
            <Avatar radius="md" size={72}>{getInitialsFromName(requesterName)}</Avatar>
            <Stack gap={6}>
              <Title order={4}>{requesterName}</Title>
              <Group gap={6} wrap="wrap">
                <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(request.statusColor)}>
                  {request.statusName ?? `Status ${request.requestStatusID}`}
                </Badge>
                <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(request.needColor)}>
                  {request.needName ?? 'Need'}
                </Badge>
                {request.isExistingMember
                  ? <Badge variant="outline" color="teal">Existing member</Badge>
                  : <Badge variant="outline" color="gray">New contact</Badge>}
              </Group>
              <Text size="sm" c="dimmed">
                {request.reference ?? `Request #${request.requestID}`}
                {request.ageDisplay ? ` · submitted ${request.ageDisplay} ago` : ''}
              </Text>
            </Stack>
          </Group>

          {/* The top-right corner is the primary action's, per the house convention. */}
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {isPending ? (
              <>
                <Button variant="light" color="red" onClick={() => setRejectOpen(true)}>Reject</Button>
                <Button leftSection={<IconCheck size={16} />} onClick={() => setApproveOpen(true)}>
                  Approve &amp; create case
                </Button>
              </>
            ) : request.caseID ? (
              <Button
                variant="light"
                onClick={() => navigate(`/Counselling/Cases/${request.caseID}`)}
              >
                View case #{request.caseID}
              </Button>
            ) : null}
          </Group>
        </Group>
      </Card>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={16} />} style={tabStyle(activeTab === 'overview')}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="intake" leftSection={<IconUsers size={16} />} style={tabStyle(activeTab === 'intake')}>
            Intake
          </Tabs.Tab>
          {isDecided && (
            <Tabs.Tab value="decision" leftSection={<IconHeartHandshake size={16} />} style={tabStyle(activeTab === 'decision')}>
              Decision
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Card withBorder radius="md" padding="lg" mt="sm">
          <Tabs.Panel value="overview" pt="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Requester</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow icon={<IconUser size={18} />} label="Name" value={requesterName} />
                  {/* ⚠️ Stated on the Requester card, because "requester" and "counselee" are the
                      same person only when isForSelf is true. */}
                  <DetailRow
                    icon={<IconUsers size={18} />}
                    label="Requesting for"
                    value={request.isForSelf === false
                      ? (
                        <Badge size="sm" variant="light" color="orange">
                          {onBehalfName || 'Someone else'}
                        </Badge>
                      )
                      : <Badge size="sm" variant="light" color="teal">Themselves</Badge>}
                  />
                  <DetailRow icon={<IconMail size={18} />} label="Email" value={request.email} />
                  <DetailRow icon={<IconPhone size={18} />} label="Phone" value={request.phone} />
                  <DetailRow icon={<IconUser size={18} />} label="Gender" value={request.gender} />
                  <DetailRow
                    icon={<IconMail size={18} />}
                    label="Preferred contact"
                    value={request.preferredContactMethod}
                  />
                </Stack>
              </Card>

              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Request</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow icon={<IconHeartHandshake size={18} />} label="Need" value={request.needName} />
                  <DetailRow icon={<IconMapPin size={18} />} label="Area" value={request.areaName} />
                  <DetailRow icon={<IconMapPin size={18} />} label="Zone" value={request.zoneName} />
                  <DetailRow
                    icon={<IconMapPin size={18} />}
                    label="Location"
                    // `locationColor` landed in SDK 0.0.50, so this chips like the case detail does.
                    value={request.locationName
                      ? (
                        <Badge
                          variant="filled"
                          styles={badgeStyles}
                          style={brandBadgeStyle(request.locationColor)}
                        >
                          {request.locationName}
                        </Badge>
                      )
                      : undefined}
                  />
                  <DetailRow
                    icon={<IconCalendar size={18} />}
                    label="Submitted"
                    value={formatSDKDate(request.submittedDateTime)}
                  />
                  <DetailRow
                    icon={<IconInfoCircle size={18} />}
                    label="Source"
                    value={request.source}
                  />
                  <DetailRow
                    icon={request.hasConsent ? <IconCheck size={18} /> : <IconX size={18} />}
                    label="Consent given"
                    value={
                      <Badge size="sm" variant="light" color={request.hasConsent ? 'green' : 'red'}>
                        {request.hasConsent ? 'Yes' : 'No'}
                      </Badge>
                    }
                  />
                </Stack>
              </Card>
            </SimpleGrid>

            <Card withBorder radius="md" padding="lg" mt="lg">
              <Text fw={700} mb="sm">What they said</Text>
              <Divider mb="md" />
              {description ? (
                // Plain text on purpose: anonymous public input, never rendered as HTML.
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{description}</Text>
              ) : (
                <Text size="sm" c="dimmed">No description was given.</Text>
              )}
            </Card>

            {/* The need's own custom questions, resolved to labels by the API.
                ⚠️ Rendered only when non-empty. An empty "Additional answers" panel would read as
                "they were asked and said nothing", which is a different and wrong claim: a need with
                no custom fields asks nothing at all. */}
            {customData.length > 0 && (
              <Card withBorder radius="md" padding="lg" mt="lg">
                <Text fw={700} mb="sm">Additional answers</Text>
                <Text size="sm" c="dimmed" mb="md">
                  The extra questions {request.needName ?? 'this need'} asks on the public form.
                </Text>
                <Divider mb="md" />
                <Stack gap="md">
                  {customData.map((cd) => (
                    <DetailRow
                      key={cd.needCustomFieldID}
                      icon={<IconInfoCircle size={18} />}
                      label={cd.label || cd.fieldKey || `Field ${cd.needCustomFieldID}`}
                      value={cd.value?.trim() || undefined}
                    />
                  ))}
                </Stack>
              </Card>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="intake" pt="md">
            <Stack gap="lg">
              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Who needs care</Text>
                <Text size="sm" c="dimmed" mb="md">
                  Everyone here becomes a counselee on the case when the request is approved. Select a
                  person to see their full details.
                </Text>
                <Divider mb="md" />

                {/* `subjects[]` is populated since SDK 0.0.48, so a multi-person need (a couple for
                    Marriage, a parent and child for Parenting) now shows everyone. This used to show
                    one name and carry a permanent "may be incomplete" warning. */}
                {subjects.length === 0 ? (
                  <Stack gap="md">
                    {/* ⚠️ Who the counselee is depends on `isForSelf`, not on `subjects[]` being empty.
                        On an on-behalf-of request the counselee is the staged subject, and the
                        requester is only the person who filled the form in. */}
                    <Text size="sm" c="dimmed">
                      {isOnBehalf
                        ? 'This request was submitted on behalf of someone else. The person needing care is below.'
                        : 'No separate counselees were captured, so this request is for the requester.'}
                    </Text>
                    <Card
                      withBorder
                      radius="md"
                      padding="md"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setDetailPerson(isOnBehalf ? onBehalfAsPerson : requesterAsPerson)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDetailPerson(isOnBehalf ? onBehalfAsPerson : requesterAsPerson);
                        }
                      }}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <Avatar radius="xl" size="md">
                          {getInitialsFromName(isOnBehalf ? onBehalfName : requesterName)}
                        </Avatar>
                        <div style={{ minWidth: 0 }}>
                          <Group gap={6}>
                            <Text size="sm" fw={600}>{isOnBehalf ? onBehalfName : requesterName}</Text>
                            <Badge size="xs" variant="light" color="blue">Needs care</Badge>
                            {isOnBehalf && request.subjectRole && (
                              <Badge size="xs" variant="outline">{request.subjectRole}</Badge>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {isOnBehalf
                              ? ([request.subjectEmail, request.subjectPhone].filter(Boolean).join(' · ')
                                || 'No contact details captured for them')
                              : (request.email || request.phone || 'No contact')}
                          </Text>
                        </div>
                      </Group>
                    </Card>

                    {/* On an on-behalf-of request the requester is the contact, not the counselee.
                        Showing both stops the two being read as the same person. */}
                    {isOnBehalf && (
                      <Card
                        withBorder
                        radius="md"
                        padding="md"
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setDetailPerson(requesterAsPerson)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDetailPerson(requesterAsPerson);
                          }
                        }}
                      >
                        <Group gap="sm" wrap="nowrap">
                          <Avatar radius="xl" size="md">{getInitialsFromName(requesterName)}</Avatar>
                          <div style={{ minWidth: 0 }}>
                            <Group gap={6}>
                              <Text size="sm" fw={600}>{requesterName}</Text>
                              <Badge size="xs" variant="light" color="gray">Requested by</Badge>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {request.email || request.phone || 'No contact'}
                            </Text>
                          </div>
                        </Group>
                      </Card>
                    )}
                  </Stack>
                ) : (
                  <Stack gap="sm">
                    {subjects.map((sub) => {
                      const name = sub.fullName
                        || [sub.firstName, sub.surname].filter(Boolean).join(' ').trim()
                        || 'Unnamed';
                      return (
                        <Card
                          key={sub.subjectID}
                          withBorder
                          radius="md"
                          padding="md"
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setDetailPerson(toPerson(sub))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setDetailPerson(toPerson(sub));
                            }
                          }}
                        >
                          <Group justify="space-between" align="flex-start" wrap="nowrap">
                            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                              <Avatar radius="xl" size="md">{getInitialsFromName(name)}</Avatar>
                              <div style={{ minWidth: 0 }}>
                                <Group gap={6}>
                                  <Text size="sm" fw={600}>{name}</Text>
                                  {sub.isMain && <Badge size="xs" variant="light" color="blue">Main</Badge>}
                                  {sub.role && <Badge size="xs" variant="outline">{sub.role}</Badge>}
                                </Group>
                                <Text size="xs" c="dimmed" truncate>
                                  {[sub.email, sub.phone].filter(Boolean).join(' · ') || 'No contact'}
                                </Text>
                                {/* Server-computed affiliation — location, other church, or
                                    "Not a member". Not derived here, so both queues word it alike. */}
                                {sub.displayLocationName && (
                                  <Text size="xs" c="dimmed" truncate>{sub.displayLocationName}</Text>
                                )}
                              </div>
                            </Group>
                            <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                              {sub.gender && <Badge size="xs" variant="outline">{sub.gender}</Badge>}
                              {sub.dateOfBirth && (
                                <Badge size="xs" variant="outline" color="gray">
                                  {formatBirthDate(sub.dateOfBirth) ?? sub.dateOfBirth}
                                </Badge>
                              )}
                              <Badge size="xs" variant="light" color={sub.memberID ? 'teal' : 'gray'}>
                                {sub.memberName || (sub.memberID ? `Member #${sub.memberID}` : 'New contact')}
                              </Badge>
                            </Group>
                          </Group>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Card>

              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Matching</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow
                    icon={<IconUser size={18} />}
                    label="Matched member"
                    value={request.matchedMemberName}
                  />
                  <DetailRow
                    icon={<IconUsers size={18} />}
                    label="Same-gender leader preferred"
                    value={
                      <Badge size="sm" variant="light" color={request.preferSameGenderLeader ? 'yellow' : 'gray'}>
                        {request.preferSameGenderLeader ? 'Yes' : 'No'}
                      </Badge>
                    }
                  />
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          {isDecided && (
            <Tabs.Panel value="decision" pt="md">
              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Decision</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow
                    icon={
                      <Avatar src={resolveAvatarSrc(request.decidedByAvatar)} size={18} radius="xl">
                        {getInitialsFromName(request.decidedByName ?? '')}
                      </Avatar>
                    }
                    label="Decided by"
                    value={request.decidedByName}
                  />
                  <DetailRow
                    icon={<IconCalendar size={18} />}
                    label="Decided on"
                    value={request.decidedDateTime ? formatSDKDate(request.decidedDateTime) : undefined}
                  />
                  <DetailRow
                    icon={<IconInfoCircle size={18} />}
                    label="Reason"
                    value={request.decisionReason}
                  />
                  {request.caseID && (
                    <DetailRow
                      icon={<IconHeartHandshake size={18} />}
                      label="Case created"
                      value={
                        <Button
                          size="compact-sm"
                          variant="light"
                          onClick={() => navigate(`/Counselling/Cases/${request.caseID}`)}
                        >
                          Case #{request.caseID}
                        </Button>
                      }
                    />
                  )}
                </Stack>
              </Card>
            </Tabs.Panel>
          )}
        </Card>
      </Tabs>

      <ApproveRequestModal
        request={request}
        opened={approveOpen}
        onClose={() => setApproveOpen(false)}
        onApproved={handleApproved}
      />
      <RejectRequestModal
        request={request}
        opened={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onRejected={handleRejected}
      />
      <SubmittedPersonModal
        person={detailPerson}
        opened={detailPerson !== null}
        onClose={() => setDetailPerson(null)}
        contextLabel="request"
        hasConsent={request.hasConsent}
      />
    </Stack>
  );
}
